import * as XLSX from 'xlsx';
import type { Dictamen } from '../views/Dictamenes/types';

export interface EstadisticasReporte {
  totalDictamenes: number;
  dictamenesRiesgo: number;
  dictamenesHabitabilidad: number;
  dictamenesSeguridad: number;
  montoTotal: number;
  porcentajeNoPagado: number;
}

export class ReporteService {
  static calcularEstadisticas(dictamenes: Dictamen[]): EstadisticasReporte {
    const montoTotal = dictamenes.reduce((sum, d) => sum + Number(d.montoPago || 0), 0);
    const noPagados = dictamenes.filter(d => d.estatusPago !== 'Pagado').length;
    const porcentajeNoPagado = dictamenes.length > 0 ? (noPagados / dictamenes.length) * 100 : 0;
    
    return {
      totalDictamenes: dictamenes.length,
      dictamenesRiesgo: dictamenes.filter(d => d.tipo?.toLowerCase().includes('riesgo')).length,
      dictamenesHabitabilidad: dictamenes.filter(d => d.tipo?.toLowerCase().includes('habitabilidad')).length,
      dictamenesSeguridad: dictamenes.filter(d => d.tipo?.toLowerCase().includes('seguridad')).length,
      montoTotal,
      porcentajeNoPagado
    };
  }

  static formatearRangoFechas(fechaInicio?: string, fechaFin?: string, fechaIndividual?: string): string {
    if (fechaIndividual) {
      return `Fecha: ${new Date(fechaIndividual).toLocaleDateString('es-ES')}`;
    }
    
    if (fechaInicio && fechaFin) {
      return `Del ${new Date(fechaInicio).toLocaleDateString('es-ES')} al ${new Date(fechaFin).toLocaleDateString('es-ES')}`;
    }
    
    if (fechaInicio) {
      return `Desde el ${new Date(fechaInicio).toLocaleDateString('es-ES')}`;
    }
    
    if (fechaFin) {
      return `Hasta el ${new Date(fechaFin).toLocaleDateString('es-ES')}`;
    }
    
    return 'Todos los registros';
  }

  static async generarPDF(dictamenes: Dictamen[], filtros: any): Promise<Blob> {
    // Usar el generador de PDF similar al de multas
    const { generateReportePDF } = await import('../utils/reportePdfGenerator');
    
    const estadisticas = this.calcularEstadisticas(dictamenes);
    const rangoFechas = this.formatearRangoFechas(
      filtros.fechaInicio, 
      filtros.fechaFin, 
      filtros.fechaIndividual
    );
    
    const fechaGeneracion = new Date().toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    const datosReporte = {
      dictamenes,
      estadisticas,
      rangoFechas,
      fechaGeneracion
    };

    // Generar PDF usando el mismo patrón que las multas
    await generateReportePDF(datosReporte);
    
    // Retornar un blob vacío ya que el PDF se abre en nueva ventana
    return new Blob([''], { type: 'application/pdf' });
  }



  static generarExcel(dictamenes: Dictamen[], filtros: any): void {
    const estadisticas = this.calcularEstadisticas(dictamenes);
    const rangoFechas = this.formatearRangoFechas(
      filtros.fechaInicio, 
      filtros.fechaFin, 
      filtros.fechaIndividual
    );
    const fechaGeneracion = new Date().toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    // Crear libro de trabajo
    const wb = XLSX.utils.book_new();
    
    // Crear hoja principal con plantilla
    const ws = XLSX.utils.aoa_to_sheet([]);
    
    // Header con log o (placeholder) y título
    XLSX.utils.sheet_add_aoa(ws, [
      ['', '', '', 'REPORTE DE DICTÁMENES EMITIDOS'], 
      ['', '', '', 'H. Ayuntamiento de Totolac, Tlaxcala'],
      ['', '', '', `Generado el: ${fechaGeneracion}`],
      ['', '', '', rangoFechas],
      [''], // Fila vacía
      
      // Estadísticas en formato de tabla
      ['DESGLOSE ESTADÍSTICO', '', '', ''],
      ['Total de Dictámenes Emitidos:', estadisticas.totalDictamenes, '', ''],
      ['Dictámenes de Riesgo:', estadisticas.dictamenesRiesgo, '', ''],
      ['Dictámenes de Habitabilidad:', estadisticas.dictamenesHabitabilidad, '', ''],
      ['Dictámenes de Seguridad y Prevención:', estadisticas.dictamenesSeguridad, '', ''],
      ['Monto Total Cobrado:', `$${estadisticas.montoTotal.toFixed(2)}`, '', ''],
      ['Porcentaje No Pagado:', `${estadisticas.porcentajeNoPagado.toFixed(1)}%`, '', ''],
      [''], // Fila vacía
      
      // Headers de la tabla de datos
      ['DATOS DETALLADOS', '', '', '', '', '', '', '', '', ''],
      ['Folio', 'Tipo', 'Solicitante', 'Fecha Solicitud', 'Operador', 'Dirección', 'Colonia', 'Estado Pago', 'Monto', 'Observaciones']
    ], { origin: 'A1' });
    
    // Agregar datos de dictámenes
    const datosTabla = dictamenes.map(d => [
      d.folio || `ID-${d.id}`,
      d.tipo || 'N/A',
      d.nombre || 'N/A',
      d.fechaSolicitud ? new Date(d.fechaSolicitud).toLocaleDateString('es-ES') : 'N/A',
      d.operador || 'N/A',
      d.direccion || 'N/A',
      d.colonia || 'N/A',
      d.estatusPago || 'No pagado',
      Number(d.montoPago || 0),
      d.observaciones || 'N/A'
    ]);
    
    XLSX.utils.sheet_add_aoa(ws, datosTabla, { origin: 'A16' });
    
    // Configurar anchos de columna
    ws['!cols'] = [
      { wch: 15 }, // Folio
      { wch: 15 }, // Tipo
      { wch: 25 }, // Solicitante
      { wch: 15 }, // Fecha
      { wch: 20 }, // Operador
      { wch: 30 }, // Dirección
      { wch: 20 }, // Colonia
      { wch: 15 }, // Estado Pago
      { wch: 12 }, // Monto
      { wch: 30 }  // Observaciones
    ];
    
    // Aplicar estilos (si la librería lo soporta)
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
    
    // Agregar la hoja al libro
    XLSX.utils.book_append_sheet(wb, ws, 'Reporte Dictámenes');

    // Descargar archivo
    const nombreArchivo = `Reporte_Dictamenes_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, nombreArchivo);
  }

  static descargarPDF(blob: Blob, nombreArchivo?: string): void {
    // El PDF ya se abrió en nueva ventana, no necesitamos descargar
    console.log('PDF abierto en nueva ventana para impresión');
  }
}